// ------------------------- IMPORTS -------------------------
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Checkbox,
  TextField,
  InputAdornment,
  Autocomplete,
  Grid,
  Card,
  Button,
  Alert,
  Link,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useLocation, useNavigate } from "react-router-dom";
import SideBar from "../Component/SideBar";
import useUncategorizedGroupedApi from "../Api/useUncategorizedGroupedApi";
import useSetCategoryApi from "../Api/setCategoryApi";
import { CATEGORY_LIST } from "../Component/CategoryList";

const SetCategory = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const domain = location.state?.domain;

  const { getUncategorizedGrouped, groups, loading } =
    useUncategorizedGroupedApi();

  const {
    setCategory,
    result,
    loading: setLoading,
    error,
    setError,
  } = useSetCategoryApi();

  const [domainData, setDomainData] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);

  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  const [search, setSearch] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);

  // Confirmation Dialog
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  // Extract selected URLs
  const selectedUrlsList = domainData
    .filter((entry) => selectedRows.includes(entry._id))
    .map((entry) => entry.url);

  // Pagination for URLs in dialog
  const [dialogPage, setDialogPage] = useState(0);
  const dialogRowsPerPage = 25;
  const paginatedUrls = selectedUrlsList.slice(
    dialogPage * dialogRowsPerPage,
    dialogPage * dialogRowsPerPage + dialogRowsPerPage
  );

  // --------------------- FETCH DATA ----------------------
  useEffect(() => {
    if (!domain) return;
    getUncategorizedGrouped({
      pageNum: 1,
      pageSize: 9999,
      isProcessed: false,
    });
  }, [domain]);

  useEffect(() => {
    if (!domain || groups.length === 0) return;
    const selected = groups.find((g) => g.domain === domain);
    if (selected) {
      setDomainData(selected.entries);
    }
  }, [groups, domain]);

  // --------------------- FILTERED DATA ----------------------
  const filteredData = domainData.filter((item) =>
    item.url.toLowerCase().includes(search.toLowerCase())
  );

  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const isAllSelected =
    paginatedData.length > 0 &&
    paginatedData.every((item) => selectedRows.includes(item._id));

  const handleSelectAll = () => {
    if (isAllSelected) {
      const remaining = selectedRows.filter(
        (id) => !paginatedData.some((item) => item._id === id)
      );
      setSelectedRows(remaining);
    } else {
      const newIds = paginatedData
        .map((item) => item._id)
        .filter((id) => !selectedRows.includes(id));
      setSelectedRows([...selectedRows, ...newIds]);
    }
  };

  const handleSelectRow = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // ---------------------- CONFIRM SUBMIT ---------------------
  const handleOpenConfirmDialog = () => {
    if (selectedCategories.length === 0) {
      setError("Please select at least one category");
      return;
    }
    if (selectedRows.length === 0) {
      setError("Please select at least one URL");
      return;
    }
    setDialogPage(0); // Reset dialog pagination
    setOpenConfirmDialog(true);
  };

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
  };

  // ---------------------- FINAL SUBMIT ----------------------
  const handleSetCategory = async () => {
    setOpenConfirmDialog(false);

    try {
      setError(null);

      const categoryIds = selectedCategories.map((c) => c.id);

      let success = 0;
      let fail = 0;

      for (const url of selectedUrlsList) {
        const payload = { url, categories: categoryIds };

        try {
          await setCategory(payload);
          success++;
        } catch (err) {
          fail++;
        }
      }

      if (success > 0) {
        setSelectedRows([]);
        setSelectedCategories([]);
        navigate("/UncategorizedGrouped"); // Navigate after success
      }

      if (fail > 0) {
        setError(`${fail} URL(s) failed to update`);
      }
    } catch (err) {
      setError("Failed to set category. Please try again.");
    }
  };

  // --------------------- UI START ------------------------
  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <SideBar />

      <Box component="main" className="main-box">
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          Uncategorized URLs
        </Typography>

        <Paper sx={{ p: 3 }}>
          {/* TOP CARDS */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 2, borderRadius: 2 }}>
                <Typography>
                  <b>Domain:</b> {domain}
                </Typography>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ p: 2, borderRadius: 2 }}>
                <Typography>
                  <b>Total URLs:</b>{" "}
                  {groups.find((g) => g.domain === domain)?.count || 0}
                </Typography>
              </Card>
            </Grid>
          </Grid>

          {/* ERROR */}
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {/* SUCCESS */}
          {result?.success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {result.message || "Category applied successfully"}
            </Alert>
          )}

          {/* CATEGORY DROPDOWN */}
          <Autocomplete
            multiple
            options={CATEGORY_LIST}
            value={selectedCategories}
            onChange={(e, value) => setSelectedCategories(value)}
            getOptionLabel={(option) => `(${option.id}) ${option.name}`}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Categories"
                placeholder="Choose..."
                size="small"
                sx={{ mb: 2 }}
              />
            )}
            sx={{ mb: 2 }}
          />

          {/* SEARCH */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search URLs..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          {/* LOADING */}
          {loading && (
            <Box sx={{ textAlign: "center", py: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {/* TABLE */}
          {!loading && filteredData.length > 0 && (
            <>
              <Table>
                <TableHead>
                  <TableRow sx={{ background: "#f5f5f5" }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isAllSelected}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell>#</TableCell>
                    <TableCell>URL</TableCell>
                    <TableCell>Created At</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {paginatedData.map((entry, idx) => (
                    <TableRow key={entry._id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedRows.includes(entry._id)}
                          onChange={() => handleSelectRow(entry._id)}
                        />
                      </TableCell>

                      <TableCell>{page * rowsPerPage + idx + 1}</TableCell>

                      <TableCell>
                        <Link
                          href={entry.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: "primary.main",
                            "&:hover": { textDecoration: "underline" },
                          }}
                        >
                          {entry.url}
                        </Link>
                      </TableCell>

                      <TableCell>
                        {new Date(entry.created_at).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <TablePagination
                component="div"
                count={filteredData.length}
                page={page}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[rowsPerPage]}
                onPageChange={(e, newPage) => setPage(newPage)}
              />
            </>
          )}

          {!loading && filteredData.length === 0 && (
            <Typography
              sx={{ textAlign: "center", py: 3 }}
              color="text.secondary"
            >
              No data found.
            </Typography>
          )}

          {/* SUBMIT BUTTON */}
          {selectedRows.length > 0 && selectedCategories.length > 0 && (
            <Box sx={{ textAlign: "right", mt: 3 }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                disabled={setLoading ? true : false}
                onClick={handleOpenConfirmDialog}
              >
                {setLoading ? "Applying..." : "Set Category"}
              </Button>
            </Box>
          )}
        </Paper>
      </Box>

      {/* CONFIRMATION DIALOG */}
      <Dialog open={openConfirmDialog} onClose={handleCloseConfirmDialog}>
        <DialogTitle sx={{ fontWeight: "bold" }}>
          Confirm Category Assignment
        </DialogTitle>

        <DialogContent>
          <DialogContentText>
            <Typography sx={{ fontWeight: "bold" }}>
              Selected Categories:
            </Typography>

            <Typography color="text.secondary" sx={{ mb: 2 }}>
              {selectedCategories.map((c) => `(${c.id}) ${c.name}`).join(", ")}
            </Typography>

            <Typography sx={{ fontWeight: "bold", mb: 1 }}>
              Selected URLs ({selectedUrlsList.length})
            </Typography>

            {/* URL LIST */}
            <Box
              sx={{
                maxHeight: 300,
                overflowY: "auto",
                paddingBlock: 1,
                paddingInline: 4,
                border: "1px solid #ddd",
                borderRadius: 2,
                background: "#fafafa",
              }}
            >
              {paginatedUrls.map((url, i) => (
                <Typography key={i} sx={{ mb: 1 }}>
                  {dialogPage * dialogRowsPerPage + i + 1}. {url}
                </Typography>
              ))}
            </Box>

            {/* Pagination control */}
            {selectedUrlsList.length > dialogRowsPerPage && (
              <TablePagination
                component="div"
                count={selectedUrlsList.length}
                page={dialogPage}
                onPageChange={(e, newPage) => setDialogPage(newPage)}
                rowsPerPage={dialogRowsPerPage}
                rowsPerPageOptions={[dialogRowsPerPage]}
              />
            )}
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={handleCloseConfirmDialog}>
            Cancel
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={handleSetCategory}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SetCategory;
